import { useEffect, useRef, useState } from "react";

/* ══════════════════════════════════════════════════════════════════════════
   StudoX — CINEMATIC LAUNCH SEQUENCE  ///  "Ignition"  (Triple-A rebuild)
   ──────────────────────────────────────────────────────────────────────────
   A single-shot boot cinematic played on first login (replayable from
   Settings). One heavy WebGL-free canvas carries the spectacle (warp dive,
   particle logo-assembly, shockwaves, ambient orbit), crisp DOM carries the
   typography (logo, wordmark light-sweep, HUD, personalised welcome).

   Public API unchanged:  <LaunchAnimation user onDone platform="flow"|"core" />
   ══════════════════════════════════════════════════════════════════════════ */

/* ── keyframes (injected once) ─────────────────────────────────────────────── */
const KF = `
@keyframes lx-logo-in   { 0%{transform:scale(0) rotate(-40deg);opacity:0;filter:blur(20px)} 55%{transform:scale(1.14) rotate(4deg);opacity:1;filter:blur(0)} 78%{transform:scale(.96) rotate(-1.5deg)} 100%{transform:scale(1) rotate(0)} }
@keyframes lx-logo-glow { 0%,100%{filter:drop-shadow(0 0 14px var(--g1)) drop-shadow(0 0 30px var(--g2))} 50%{filter:drop-shadow(0 0 34px var(--g1)) drop-shadow(0 0 70px var(--g2)) drop-shadow(0 0 110px var(--g1))} }
@keyframes lx-char      { 0%{opacity:0;transform:translateY(16px) scale(.7);filter:blur(8px)} 60%{opacity:1;filter:blur(0)} 100%{opacity:1;transform:translateY(0) scale(1)} }
@keyframes lx-scan      { 0%{transform:translateX(-140%)} 100%{transform:translateX(140%)} }
@keyframes lx-hud-l     { from{opacity:0;transform:translateX(-24px)} to{opacity:1;transform:translateX(0)} }
@keyframes lx-hud-r     { from{opacity:0;transform:translateX(24px)} to{opacity:1;transform:translateX(0)} }
@keyframes lx-corner    { 0%{opacity:0;transform:scale(.4)} 100%{opacity:1;transform:scale(1)} }
@keyframes lx-sub       { from{opacity:0;letter-spacing:14px} to{opacity:1;letter-spacing:6px} }
@keyframes lx-card-up   { 0%{opacity:0;transform:translateY(30px) scale(.94)} 100%{opacity:1;transform:translateY(0) scale(1)} }
@keyframes lx-bar       { from{width:0} to{width:100%} }
@keyframes lx-fade-out  { from{opacity:1} to{opacity:0} }
@keyframes lx-flash     { 0%{opacity:0} 8%{opacity:.95} 100%{opacity:0} }
@keyframes lx-badge-pop { 0%{opacity:0;transform:scale(.6)} 60%{transform:scale(1.12)} 100%{opacity:1;transform:scale(1)} }
@keyframes lx-ring-pulse{ 0%,100%{opacity:.5;transform:translate(-50%,-50%) scale(1)} 50%{opacity:1;transform:translate(-50%,-50%) scale(1.05)} }
@keyframes lx-tick      { 0%,100%{opacity:.3} 50%{opacity:1} }
@keyframes lx-flicker   { 0%,100%{opacity:1} 46%{opacity:.82} 48%{opacity:.5} 52%{opacity:.92} }
`;

function injectKF() {
  if (typeof document === "undefined" || document.getElementById("__lx_kf")) return;
  const s = document.createElement("style");
  s.id = "__lx_kf";
  s.textContent = KF;
  document.head.appendChild(s);
}

/* ── role theming ─────────────────────────────────────────────────────────── */
const ROLE = {
  student: { label: "SCHÜLER:IN", color: "#8b5cf6", hud: ["LERNMODUS: AKTIV", "ACHIEVEMENTS: BEREIT", "STREAK: GELADEN"] },
  teacher: { label: "LEHRER:IN",  color: "#06b6d4", hud: ["ADMIN-ZUGANG: AKTIV", "KLASSEN: GELADEN", "REVIEW-QUEUE: OK"] },
  admin:   { label: "ADMIN",      color: "#ec4899", hud: ["ROOT-ZUGANG: AKTIV", "SYSTEM: BEREIT", "OVERSEER: LINK OK"] },
  solid_ground: { label: "OVERSEER", color: "#f59e0b", hud: ["SOLID GROUND: AKTIV", "SYSTEM: BEREIT", "ALLE RECHTE: OK"] },
  parent:  { label: "ELTERN",     color: "#fbbf24", hud: ["ELTERN-PORTAL: AKTIV", "BERICHTE: BEREIT", "KINDER: VERKNÜPFT"] },
  mentor:  { label: "MENTOR:IN",  color: "#22c55e", hud: ["MENTOR-MODUS: AKTIV", "SCHÜLER: GELADEN", "CHAT: LIVE"] },
};

const PALETTE = ["#8b5cf6", "#06b6d4", "#22e3ff", "#ec4899", "#a78bfa", "#ffffff"];

/* ── logo marks (SVG) ─────────────────────────────────────────────────────── */
export function NexusMark({ size = 120 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 120 120" fill="none">
      <defs>
        <linearGradient id="lxnm-top" x1="60" y1="16" x2="60" y2="60" gradientUnits="userSpaceOnUse"><stop stopColor="#ede9fe"/><stop offset="1" stopColor="#a78bfa"/></linearGradient>
        <linearGradient id="lxnm-left" x1="24" y1="37" x2="60" y2="104" gradientUnits="userSpaceOnUse"><stop stopColor="#7c3aed"/><stop offset="1" stopColor="#2e0a6e"/></linearGradient>
        <linearGradient id="lxnm-right" x1="96" y1="37" x2="60" y2="104" gradientUnits="userSpaceOnUse"><stop stopColor="#22d3ee"/><stop offset="1" stopColor="#075985"/></linearGradient>
        <radialGradient id="lxnm-core" cx="50%" cy="50%" r="50%"><stop stopColor="#fff" stopOpacity=".7"/><stop offset=".5" stopColor="#a78bfa" stopOpacity=".25"/><stop offset="1" stopColor="#8b5cf6" stopOpacity="0"/></radialGradient>
      </defs>
      <polygon points="60,16 96,38 60,60 24,38" fill="url(#lxnm-top)"/>
      <polygon points="24,38 60,60 60,104 24,82" fill="url(#lxnm-left)"/>
      <polygon points="96,38 60,60 60,104 96,82" fill="url(#lxnm-right)"/>
      <polyline points="60,16 96,38 96,82 60,104 24,82 24,38 60,16" stroke="rgba(255,255,255,0.22)" strokeWidth="1.3" fill="none"/>
      <line x1="60" y1="16" x2="60" y2="60" stroke="rgba(255,255,255,0.34)" strokeWidth="1.3"/>
      <line x1="24" y1="38" x2="60" y2="60" stroke="rgba(255,255,255,0.16)" strokeWidth="1.3"/>
      <line x1="96" y1="38" x2="60" y2="60" stroke="rgba(255,255,255,0.16)" strokeWidth="1.3"/>
      <circle cx="60" cy="60" r="13" fill="url(#lxnm-core)"/>
      {[[60,16],[96,38],[96,82],[60,104],[24,82],[24,38]].map(([x,y],i)=>(
        <circle key={i} cx={x} cy={y} r={i===0?3.4:2.4} fill={i<2?"#c4b5fd":i<4?"#7c3aed":"#22d3ee"} opacity="0.95"/>
      ))}
      <circle cx="98" cy="22" r="6" fill="#22d3ee"/><circle cx="98" cy="22" r="2.6" fill="#fff"/>
    </svg>
  );
}

export function CoreMark({ size = 120 }) {
  const s = size / 80;
  return (
    <svg width={size} height={size} viewBox="0 0 80 80" fill="none" style={{ overflow: "visible" }}>
      <defs>
        <radialGradient id="lxcm-orb" cx="38%" cy="28%" r="70%"><stop stopColor="#fffdf5"/><stop offset=".3" stopColor="#fde68a"/><stop offset=".62" stopColor="#a855f7"/><stop offset="1" stopColor="#2e1065"/></radialGradient>
        <linearGradient id="lxcm-portal" x1="0" y1="0" x2="1" y2="1"><stop stopColor="#fbbf24"/><stop offset=".4" stopColor="#c084fc"/><stop offset=".7" stopColor="#8b5cf6"/><stop offset="1" stopColor="#22d3ee"/></linearGradient>
      </defs>
      <circle cx="40" cy="40" r="30" fill="none" stroke="url(#lxcm-portal)" strokeWidth={3.4*s} opacity="0.92"/>
      <circle cx="40" cy="40" r="30" fill="none" stroke="rgba(255,255,255,0.35)" strokeWidth={0.7*s}/>
      {Array.from({length:16}).map((_,i)=>{const a=(i/16)*Math.PI*2;return(<line key={i} x1={40+27.2*Math.cos(a)} y1={40+27.2*Math.sin(a)} x2={40+32.8*Math.cos(a)} y2={40+32.8*Math.sin(a)} stroke="rgba(255,255,255,0.22)" strokeWidth={0.6*s}/>);})}
      <polygon points="40,27.5 49,33.5 49,46.5 40,52.5 31,46.5 31,33.5" fill="url(#lxcm-orb)" stroke="rgba(255,255,255,0.28)" strokeWidth={0.6*s}/>
      <circle cx="40" cy="40" r={4.2*s} fill="rgba(255,255,255,0.85)"/><circle cx="40" cy="40" r={1.8*s} fill="#fff"/>
    </svg>
  );
}

/* ══════════════════════════════════════════════════════════════════════════
   THE CANVAS — the whole spectacle, phase-driven, one rAF loop.
   ══════════════════════════════════════════════════════════════════════════ */
function useCinemaCanvas(phaseRef, accent) {
  const ref = useRef(null);
  useEffect(() => {
    const cv = ref.current;
    if (!cv) return;
    const ctx = cv.getContext("2d");
    if (!ctx) return;
    const DPR = Math.min(window.devicePixelRatio || 1, 2);
    let W = 0, H = 0, CX = 0, CY = 0, raf = 0, t = 0, last = performance.now();

    const resize = () => {
      W = cv.clientWidth; H = cv.clientHeight;
      cv.width = W * DPR; cv.height = H * DPR;
      ctx.setTransform(DPR, 0, 0, DPR, 0, 0);
      CX = W / 2; CY = H / 2;
    };
    resize();
    window.addEventListener("resize", resize);

    /* warp stars — 3D points rushing toward the camera */
    const WARP = Array.from({ length: 340 }, () => ({
      x: (Math.random() - 0.5) * 2,
      y: (Math.random() - 0.5) * 2,
      z: Math.random(),
      c: PALETTE[(Math.random() * PALETTE.length) | 0],
    }));

    /* convergence fragments — spawn on a wide ring, fly to center at genesis */
    const FRAG = Array.from({ length: 140 }, () => {
      const a = Math.random() * Math.PI * 2;
      const d = 0.55 + Math.random() * 0.6;
      return { a, d, sp: 0.6 + Math.random() * 0.8, c: PALETTE[(Math.random() * PALETTE.length) | 0], r: 0.6 + Math.random() * 1.8 };
    });

    /* shockwave rings + debris, fired at genesis */
    let shockAt = -1;
    const debris = [];
    let debrisFired = false;

    /* ambient orbit sparks (phase 3+) */
    const ORBIT = Array.from({ length: 46 }, (_, i) => ({
      base: 60 + Math.random() * 120,
      a: Math.random() * Math.PI * 2,
      sp: (Math.random() > 0.5 ? 1 : -1) * (0.1 + Math.random() * 0.4),
      r: 0.6 + Math.random() * 1.6,
      c: PALETTE[(Math.random() * PALETTE.length) | 0],
      wob: Math.random() * Math.PI * 2,
    }));

    const hexA = (hex, a) => {
      const h = hex.replace("#", "");
      const r = parseInt(h.slice(0, 2), 16), g = parseInt(h.slice(2, 4), 16), b = parseInt(h.slice(4, 6), 16);
      return `rgba(${r},${g},${b},${a})`;
    };

    const frame = (now) => {
      const dt = Math.min(0.05, (now - last) / 1000);
      last = now; t += dt;
      const phase = phaseRef.current;
      ctx.clearRect(0, 0, W, H);

      /* ─ Phase 0: the igniting spark ─ */
      if (phase === 0) {
        const p = Math.min(1, t / 0.3);
        ctx.globalAlpha = p;
        const g = ctx.createRadialGradient(CX, CY, 0, CX, CY, 60 + p * 40);
        g.addColorStop(0, "#fff"); g.addColorStop(0.4, hexA(accent, 0.6)); g.addColorStop(1, "transparent");
        ctx.fillStyle = g; ctx.beginPath(); ctx.arc(CX, CY, 60 + p * 40, 0, 7); ctx.fill();
        ctx.globalAlpha = 1;
      }

      /* ─ Phase 1: WARP dive ─ */
      if (phase === 1 || phase === 2) {
        const warpSpeed = phase === 1 ? 1.1 : 0.35;
        for (const s of WARP) {
          s.z -= warpSpeed * dt;
          if (s.z <= 0.02) { s.x = (Math.random() - 0.5) * 2; s.y = (Math.random() - 0.5) * 2; s.z = 1; }
          const sx = CX + (s.x / s.z) * W * 0.55;
          const sy = CY + (s.y / s.z) * H * 0.55;
          const pz = s.z + warpSpeed * dt;
          const psx = CX + (s.x / pz) * W * 0.55;
          const psy = CY + (s.y / pz) * H * 0.55;
          const a = Math.min(1, (1 - s.z) * 1.6);
          ctx.strokeStyle = hexA(s.c, a * 0.85);
          ctx.lineWidth = Math.max(0.5, (1 - s.z) * 2.4);
          ctx.beginPath(); ctx.moveTo(sx, sy); ctx.lineTo(psx, psy); ctx.stroke();
        }
        // charging vortex glow at center
        const vg = ctx.createRadialGradient(CX, CY, 0, CX, CY, 120 + Math.sin(t * 8) * 14);
        vg.addColorStop(0, hexA("#ffffff", phase === 1 ? 0.5 : 0.2));
        vg.addColorStop(0.5, hexA(accent, 0.25));
        vg.addColorStop(1, "transparent");
        ctx.fillStyle = vg; ctx.beginPath(); ctx.arc(CX, CY, 120, 0, 7); ctx.fill();
      }

      /* ─ Phase 2: GENESIS — fragments converge, shockwave, debris ─ */
      if (phase === 2) {
        if (shockAt < 0) shockAt = t + 0.15;
        const gp = Math.min(1, (t - (shockAt - 0.15)) / 0.9); // convergence 0→1
        for (const f of FRAG) {
          const d = f.d * (1 - gp * gp); // ease toward center
          const x = CX + Math.cos(f.a) * d * (W * 0.5);
          const y = CY + Math.sin(f.a) * d * (H * 0.5);
          ctx.fillStyle = hexA(f.c, 0.2 + gp * 0.7);
          ctx.beginPath(); ctx.arc(x, y, f.r * (0.5 + gp), 0, 7); ctx.fill();
          // streak toward center
          ctx.strokeStyle = hexA(f.c, gp * 0.5);
          ctx.lineWidth = 1;
          ctx.beginPath(); ctx.moveTo(x, y); ctx.lineTo(CX + Math.cos(f.a) * d * (W * 0.5) * 0.7, CY + Math.sin(f.a) * d * (H * 0.5) * 0.7); ctx.stroke();
        }
        // shockwave rings + debris burst at the lock moment
        if (t >= shockAt + 0.7) {
          if (!debrisFired) {
            debrisFired = true;
            for (let i = 0; i < 70; i++) {
              const a = Math.random() * Math.PI * 2, sp = 120 + Math.random() * 340;
              debris.push({ x: CX, y: CY, vx: Math.cos(a) * sp, vy: Math.sin(a) * sp, life: 1, c: PALETTE[(Math.random() * PALETTE.length) | 0], r: 1 + Math.random() * 2.5 });
            }
          }
          const age = t - (shockAt + 0.7);
          for (let i = 0; i < 3; i++) {
            const ra = age - i * 0.1;
            if (ra > 0 && ra < 0.9) {
              ctx.strokeStyle = hexA(i === 0 ? "#ffffff" : accent, (1 - ra / 0.9) * 0.8);
              ctx.lineWidth = 2 - i * 0.5;
              ctx.beginPath(); ctx.arc(CX, CY, ra * 460, 0, 7); ctx.stroke();
            }
          }
        }
      }

      /* debris physics (persists into phase 3) */
      for (const d of debris) {
        if (d.life <= 0) continue;
        d.x += d.vx * dt; d.y += d.vy * dt; d.vx *= 0.95; d.vy *= 0.95; d.life -= dt * 0.9;
        if (d.life <= 0) continue;
        ctx.globalAlpha = Math.max(0, d.life);
        ctx.fillStyle = d.c; ctx.shadowColor = d.c; ctx.shadowBlur = 6;
        ctx.beginPath(); ctx.arc(d.x, d.y, d.r * d.life, 0, 7); ctx.fill();
        ctx.shadowBlur = 0; ctx.globalAlpha = 1;
      }

      /* ─ Phase 3+: ambient orbit sparks + soft core aura ─ */
      if (phase >= 3) {
        const aura = ctx.createRadialGradient(CX, CY, 0, CX, CY, 200);
        aura.addColorStop(0, hexA(accent, 0.14 + Math.sin(t * 1.5) * 0.03));
        aura.addColorStop(0.6, hexA(accent, 0.04));
        aura.addColorStop(1, "transparent");
        ctx.fillStyle = aura; ctx.beginPath(); ctx.arc(CX, CY, 200, 0, 7); ctx.fill();
        for (const o of ORBIT) {
          o.a += o.sp * dt;
          const rad = o.base + Math.sin(t * 0.8 + o.wob) * 10;
          const x = CX + Math.cos(o.a) * rad;
          const y = CY + Math.sin(o.a) * rad * 0.62; // elliptical
          ctx.globalAlpha = 0.5 + 0.5 * Math.sin(t * 2 + o.wob);
          ctx.fillStyle = o.c; ctx.shadowColor = o.c; ctx.shadowBlur = 5;
          ctx.beginPath(); ctx.arc(x, y, o.r, 0, 7); ctx.fill();
        }
        ctx.shadowBlur = 0; ctx.globalAlpha = 1;
      }

      raf = requestAnimationFrame(frame);
    };
    raf = requestAnimationFrame(frame);

    return () => { cancelAnimationFrame(raf); window.removeEventListener("resize", resize); };
  }, [phaseRef, accent]);

  return ref;
}

/* ══ MAIN COMPONENT ══════════════════════════════════════════════════════════ */
export default function LaunchAnimation({ user, onDone, platform = "flow" }) {
  injectKF();

  const role = user?.role || "student";
  const cfg = ROLE[role] || ROLE.student;
  const firstName = (user?.name || "").split(" ")[0] || "Willkommen";

  const isCore = platform === "core";
  const appName = isCore ? "STUDOX CORE" : "STUDOX FLOW";
  const appSub = isCore ? "DAS LERN-ÖKOSYSTEM" : "LERNPLATTFORM DER ZUKUNFT";

  const [phase, setPhase] = useState(0);
  const [flash, setFlash] = useState(false);
  const [exiting, setExiting] = useState(false);
  const phaseRef = useRef(0);
  phaseRef.current = phase;

  const canvasRef = useCinemaCanvas(phaseRef, cfg.color);

  /* choreography — total ~6.6s */
  useEffect(() => {
    const T = [];
    T.push(setTimeout(() => setPhase(1), 340));                 // warp dive
    T.push(setTimeout(() => { setPhase(2); setFlash(true); }, 1500)); // genesis + white flash
    T.push(setTimeout(() => setFlash(false), 1720));
    T.push(setTimeout(() => setPhase(3), 3100));                // reveal (logo + wordmark)
    T.push(setTimeout(() => setPhase(4), 4700));               // welcome card
    T.push(setTimeout(() => { setExiting(true); setPhase(5); }, 5900));
    T.push(setTimeout(onDone, 6700));
    return () => T.forEach(clearTimeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const revealed = phase >= 3;

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 9999, background: "#04040e", overflow: "hidden", animation: exiting ? "lx-fade-out .8s ease forwards" : undefined }}>
      {/* the spectacle canvas */}
      <canvas ref={canvasRef} style={{ position: "absolute", inset: 0, width: "100%", height: "100%" }} />

      {/* genesis white flash */}
      {flash && <div style={{ position: "absolute", inset: 0, background: "radial-gradient(circle at 50% 50%, #fff, rgba(255,255,255,0.4) 40%, transparent 75%)", animation: "lx-flash .5s ease forwards", pointerEvents: "none" }} />}

      {/* vignette + scanlines for cinematic frame */}
      <div style={{ position: "absolute", inset: 0, pointerEvents: "none", background: "radial-gradient(ellipse 80% 70% at 50% 50%, transparent 45%, rgba(0,0,0,0.7) 100%)" }} />
      <div style={{ position: "absolute", inset: 0, pointerEvents: "none", opacity: 0.4, backgroundImage: "repeating-linear-gradient(0deg, rgba(0,0,0,0.16) 0px, rgba(0,0,0,0.16) 1px, transparent 1px, transparent 3px)", mixBlendMode: "multiply" }} />

      {/* ── center stack: logo + wordmark + role badge ─────────────────────── */}
      <div style={{ position: "absolute", left: "50%", top: "50%", transform: "translate(-50%,-50%)", display: "flex", flexDirection: "column", alignItems: "center", gap: 26 }}>
        {/* pulsing ring halo behind the logo */}
        {revealed && (
          <div style={{ position: "absolute", top: "50%", left: "50%", width: 220, height: 220, marginTop: -110, borderRadius: "50%", border: `1px solid ${cfg.color}44`, animation: "lx-ring-pulse 3s ease-in-out infinite", transform: "translate(-50%,-50%)" }} />
        )}

        {/* the logo — assembles at phase 2, locks + glows from phase 3 */}
        <div style={{ opacity: phase >= 2 ? 1 : 0, animation: phase >= 2 ? "lx-logo-in .8s cubic-bezier(.34,1.56,.64,1) forwards, lx-logo-glow 3s ease-in-out 1s infinite" : "none", "--g1": cfg.color, "--g2": `${cfg.color}55`, willChange: "transform, filter" }}>
          {isCore ? <CoreMark size={128} /> : <NexusMark size={128} />}
        </div>

        {/* scan-sweep over the freshly-forged logo */}
        {phase >= 2 && phase < 4 && (
          <div style={{ position: "absolute", top: "50%", left: "50%", width: 130, height: 130, marginTop: -85, marginLeft: -65, overflow: "hidden", borderRadius: 14, pointerEvents: "none" }}>
            <div style={{ position: "absolute", left: 0, right: 0, height: 2, top: "50%", background: `linear-gradient(90deg,transparent,${cfg.color},#fff,${cfg.color},transparent)`, animation: "lx-scan 1.1s ease .2s" }} />
          </div>
        )}

        {/* wordmark — per-letter cascade */}
        {revealed && (
          <div style={{ textAlign: "center", marginTop: -4 }}>
            <div style={{ position: "relative", overflow: "hidden", display: "inline-block" }}>
              <div style={{ fontSize: "clamp(26px,6vw,40px)", fontWeight: 900, letterSpacing: 5, lineHeight: 1, fontFamily: "Inter, system-ui, sans-serif", background: `linear-gradient(90deg,#fff,${cfg.color},#22e3ff,#fff)`, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", animation: "lx-flicker 4s ease 1s infinite" }}>
                {appName.split("").map((ch, i) => (
                  <span key={i} style={{ display: "inline-block", whiteSpace: ch === " " ? "pre" : undefined, opacity: 0, animation: `lx-char .5s cubic-bezier(.16,1,.3,1) ${0.15 + i * 0.05}s forwards` }}>{ch}</span>
                ))}
              </div>
              <div style={{ position: "absolute", inset: 0, pointerEvents: "none", background: "linear-gradient(100deg, transparent 40%, rgba(255,255,255,0.6) 50%, transparent 60%)", animation: "lx-scan 1s ease .6s forwards" }} />
            </div>
            <div style={{ marginTop: 8, fontSize: 10, letterSpacing: 6, color: "rgba(255,255,255,0.42)", fontFamily: "monospace", opacity: 0, animation: "lx-sub .7s ease .9s forwards" }}>
              {appSub}
            </div>
            {/* role badge */}
            <div style={{ marginTop: 14, display: "inline-flex", alignItems: "center", gap: 7, padding: "5px 14px", borderRadius: 99, border: `1px solid ${cfg.color}55`, background: `${cfg.color}18`, opacity: 0, animation: "lx-badge-pop .5s cubic-bezier(.34,1.56,.64,1) 1.15s forwards" }}>
              <span style={{ width: 6, height: 6, borderRadius: "50%", background: cfg.color, boxShadow: `0 0 8px ${cfg.color}`, animation: "lx-ring-pulse 1.5s ease-in-out infinite" }} />
              <span style={{ fontSize: 9.5, letterSpacing: 3, color: cfg.color, fontFamily: "monospace", fontWeight: 700 }}>{cfg.label}</span>
            </div>
          </div>
        )}

        {/* HUD telemetry — left/right of the stack */}
        {revealed && (
          <>
            <div style={{ position: "absolute", right: "calc(50% + 130px)", top: "50%", transform: "translateY(-50%)", display: "flex", flexDirection: "column", gap: 5, alignItems: "flex-end" }}>
              {cfg.hud.map((line, i) => (
                <div key={i} style={{ fontSize: 9, letterSpacing: 2, color: "rgba(255,255,255,0.3)", fontFamily: "monospace", whiteSpace: "nowrap", opacity: 0, animation: `lx-hud-l .4s ease ${0.5 + i * 0.12}s forwards` }}>
                  {line}<span style={{ color: cfg.color, marginLeft: 6 }}>›</span>
                </div>
              ))}
            </div>
            <div style={{ position: "absolute", left: "calc(50% + 130px)", top: "50%", transform: "translateY(-50%)", display: "flex", flexDirection: "column", gap: 5 }}>
              {["STUDOX-DB: OK", "SOCKET: LIVE", "AUTH: SECURE"].map((line, i) => (
                <div key={i} style={{ fontSize: 9, letterSpacing: 2, color: "rgba(255,255,255,0.3)", fontFamily: "monospace", whiteSpace: "nowrap", opacity: 0, animation: `lx-hud-r .4s ease ${0.5 + i * 0.12}s forwards` }}>
                  <span style={{ color: "#22c55e", marginRight: 6, animation: "lx-tick 1.4s ease-in-out infinite" }}>●</span>{line}
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      {/* HUD corner brackets */}
      {revealed && [
        { top: 16, left: 16, bt: 2, bl: 2 },
        { top: 16, right: 16, bt: 2, br: 2 },
        { bottom: 16, left: 16, bb: 2, bl: 2 },
        { bottom: 16, right: 16, bb: 2, br: 2 },
      ].map((c, i) => (
        <div key={i} style={{ position: "absolute", width: 30, height: 30, top: c.top, left: c.left, right: c.right, bottom: c.bottom, borderColor: cfg.color, borderTopWidth: c.bt || 0, borderBottomWidth: c.bb || 0, borderLeftWidth: c.bl || 0, borderRightWidth: c.br || 0, borderStyle: "solid", opacity: 0, animation: `lx-corner .4s ease ${0.7 + i * 0.08}s forwards` }} />
      ))}

      {/* ── welcome card ─────────────────────────────────────────────────── */}
      {phase >= 4 && (
        <div style={{ position: "absolute", bottom: "9vh", left: "50%", transform: "translateX(-50%)", width: "min(420px,88vw)", background: "rgba(10,10,28,0.9)", border: `1px solid ${cfg.color}44`, borderRadius: 20, padding: "18px 26px", backdropFilter: "blur(20px)", animation: "lx-card-up .55s cubic-bezier(.16,1,.3,1) forwards", boxShadow: `0 24px 80px rgba(0,0,0,.6), 0 0 40px ${cfg.color}18` }}>
          <p style={{ fontSize: 10.5, letterSpacing: 3, color: "rgba(255,255,255,0.4)", fontFamily: "monospace", marginBottom: 6 }}>WILLKOMMEN ZURÜCK</p>
          <p style={{ fontSize: 23, fontWeight: 800, color: "#fff", marginBottom: 12, lineHeight: 1 }}>
            {firstName} <span style={{ background: `linear-gradient(90deg,${cfg.color},#22e3ff)`, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>✦</span>
          </p>
          <div style={{ height: 3, borderRadius: 99, background: "rgba(255,255,255,0.07)", overflow: "hidden" }}>
            <div style={{ height: "100%", borderRadius: 99, background: `linear-gradient(90deg, ${cfg.color}, #22e3ff)`, animation: "lx-bar 1s cubic-bezier(.16,1,.3,1) forwards", boxShadow: `0 0 12px ${cfg.color}` }} />
          </div>
        </div>
      )}

      {/* version tag */}
      {revealed && (
        <div style={{ position: "absolute", bottom: 16, left: "50%", transform: "translateX(-50%)", fontSize: 9, letterSpacing: 3, color: "rgba(255,255,255,0.18)", fontFamily: "monospace", opacity: 0, animation: "lx-hud-r .4s ease 1s forwards" }}>
          STUDOX OS · v1.0 · {new Date().getFullYear()}
        </div>
      )}
    </div>
  );
}
